import { useNavigate, useLocation, Link } from 'react-router-dom';
import { readLoginReturn } from '../../utils/loginReturn';
import {
  Box,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Checkbox,
  Anchor,
  Group,
  Flex,
  ThemeIcon,
  Image,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  AlertCircle,
  Mail,
  Lock,
  Calendar,
  Users,
  Star,
} from 'react-feather';
import { useAuthStore } from '../../stores/auth';
import classes from './LoginPage.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

interface LoginFormValues {
  email: string;
  password: string;
  remember_me: boolean;
}

const REMEMBERED_EMAIL_KEY = 'medley_remembered_email';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();

  // Get return URL from navigation state (set by ProtectedRoute)
  const from = (location.state as { from?: string })?.from;

  // Check for remembered email on mount
  const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || '';

  const form = useForm<LoginFormValues>({
    initialValues: {
      email: rememberedEmail,
      password: '',
      remember_me: !!rememberedEmail,
    },
    validate: {
      email: value =>
        /^\S+@\S+$/.test(value) ? null : 'Invalid email address',
      password: value =>
        value.length >= 6 ? null : 'Password must be at least 6 characters',
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    clearError();

    const success = await login(values);

    if (success) {
      // Save or remove email based on remember_me
      if (values.remember_me) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, values.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      // Navigate to the page they were trying to access, or dashboard
      // MED-1348: readLoginReturn covers a 401 redirect, which has no router state.
      const returnTo = from || readLoginReturn(useAuthStore.getState().user?.id) || '/';
      navigate(returnTo, { replace: true });
    }
    // If not successful, error is already set in the auth store
  };

  return (
    <Box className={classes.wrapper}>
      {/* Background pattern */}
      <div className={classes.pattern} />

      <Flex className={classes.container}>
        {/* Left Side - Branding */}
        <Box className={classes.branding}>
          <Box
            mb={48}
            p="sm"
            style={{
              backgroundColor: '#2D4739',
              borderRadius: 8,
              display: 'inline-block',
            }}
          >
            <Image src={medleyLogoWhite} alt="Medley" h={28} w="auto" fit="contain" />
          </Box>

          <Title order={1} className={classes.headline}>
            Deliver seamless
            <br />
            partner programs
          </Title>

          <Text size="lg" c="dimmed" mt="lg" maw={400}>
            Your command center for managing partner cohorts. Schedule sessions,
            match groups, and keep every program running smoothly from kickoff
            to close.
          </Text>

          <Stack mt={48} gap="lg" className={classes.features}>
            <Group gap="md">
              <ThemeIcon
                size={36}
                radius="md"
                variant="light"
                color="medleyGreen"
              >
                <Calendar size={20} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                Coordinate sessions across partners
              </Text>
            </Group>

            <Group gap="md">
              <ThemeIcon
                size={36}
                radius="md"
                variant="light"
                color="medleyGreen"
              >
                <Users size={20} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                Match participants into balanced groups
              </Text>
            </Group>

            <Group gap="md">
              <ThemeIcon
                size={36}
                radius="md"
                variant="light"
                color="medleyGold"
              >
                <Star size={20} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                Track cohort health at a glance
              </Text>
            </Group>
          </Stack>
        </Box>

        {/* Right Side - Login Form */}
        <Box className={classes.formSection}>
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl">
            <Title order={2} mb={4}>
              Welcome back
            </Title>

            <Text c="dimmed" size="sm" mb="xl">
              Sign in to your account to continue
            </Text>

            {error && (
              <Alert
                icon={<AlertCircle size={16} />}
                title="Error"
                color="red"
                mb="md"
                onClose={clearError}
                withCloseButton
              >
                {error}
              </Alert>
            )}

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <TextInput
                  label="Email address"
                  placeholder="you@company.com"
                  leftSection={<Mail size={18} />}
                  size="md"
                  {...form.getInputProps('email')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Enter your password"
                  leftSection={<Lock size={18} />}
                  size="md"
                  {...form.getInputProps('password')}
                />

                <Group justify="space-between">
                  <Checkbox
                    label="Remember me"
                    size="sm"
                    {...form.getInputProps('remember_me', { type: 'checkbox' })}
                  />
                  <Anchor component={Link} to="/forgot-password" size="sm" c="medleyGreen.6">
                    Forgot password?
                  </Anchor>
                </Group>

                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  mt="sm"
                  loading={isLoading}
                >
                  Login as Medley Employee
                </Button>

                <Text ta="center" size="sm" c="dimmed" mt="md">
                  Logging in as someone else?
                </Text>
                <Group justify="center" gap="xs">
                  <Anchor component={Link} to="/coach/login" size="sm" c="medleyGreen.6" fw={500}>Coach</Anchor>
                  <Text size="sm" c="dimmed">|</Text>
                  <Anchor component={Link} to="/partner/login" size="sm" c="medleyGreen.6" fw={500}>Partner</Anchor>
                  <Text size="sm" c="dimmed">|</Text>
                  <Anchor component={Link} to="/participant/login" size="sm" c="medleyGreen.6" fw={500}>Participant</Anchor>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Flex>
    </Box>
  );
}

export default LoginPage;
